import { Box, Typography, Stack } from '@mui/material';
import { formatDate } from '@lib/utils';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useAppSelector } from '@app/hooks';
import { useGetDevicesQuery } from '@features/home/api';

const DeviceEventLog = () => {
  const userTenant = useAppSelector((state) => state.userTenant);
  const selectedTenant = useAppSelector((state) => state.tenant);
  const property = useAppSelector((state) => state.property);

  const currentTenantId = useMemo(
    () => (selectedTenant.currentTenant || userTenant)?.tenant_id,
    [selectedTenant.currentTenant, userTenant]
  );

  const { data: devices = [] } = useGetDevicesQuery(
    {
      tenantId: currentTenantId || '',
      propertyId: property?.property_id || '',
    },
    {
      skip: !currentTenantId || !property?.property_id,
      pollingInterval: 300000, // 5 minutes
    }
  );

  const entitiesByDevice = useMemo<Record<string, Set<string>>>(() => {
    return devices.reduce<Record<string, Set<string>>>((a, v) => ({
      ...a,
      [v.data.device_id]: new Set([
        ...(a[v.data.device_id] ? Array.from(a[v.data.device_id]) : []),
        v.data.entity_id,
      ]),
    }), {});
  }, [devices]);

  const [logs, setLogs] = useState<{ timestamp: number, text: string }[]>([]);
  const prevDevices = useRef<Record<string, Set<string>> | null>(null);

  useEffect(() => {
    if (Object.keys(entitiesByDevice).length > 0) {
      if (prevDevices.current !== null) {
        const oldDevices = new Set(Object.keys(prevDevices.current));
        const newDevices = new Set(Object.keys(entitiesByDevice));
        const oldEntities = new Set(
          Object.values(prevDevices.current)
            .reduce<string[]>((acc, es) => acc.concat(Array.from(es)), [])
        );
        const newEntities = new Set(
          Object.values(entitiesByDevice)
            .reduce<string[]>((acc, es) => acc.concat(Array.from(es)), [])
        );

        newDevices.forEach((deviceId) => {
          if (deviceId && !oldDevices.has(deviceId)) {
            setLogs(logs => [...logs, { timestamp: Date.now(), text: `New device added: ${deviceId}` }]);
          }
        });

        newEntities.forEach((entityId) => {
          if (entityId && !oldEntities.has(entityId)) {
            setLogs(logs => [...logs, { timestamp: Date.now(), text: `New device entity added: ${entityId}` }]);
          }
        });

        oldDevices.forEach((deviceId) => {
          if (deviceId && !entitiesByDevice[deviceId]) {
            setLogs(logs => [...logs, { timestamp: Date.now(), text: `Device removed: ${deviceId}` }]);
          }
        });

        oldEntities.forEach((entityId) => {
          if (entityId && !newEntities.has(entityId)) {
            setLogs(logs => [...logs, { timestamp: Date.now(), text: `Device entity removed: ${entityId}` }]);
          }
        });
      } else {
        // First load: show summary
        const systemDeviceIds = devices
          .filter(d => d.data.device_id === 'system' || d.data.device_id === property.property_id)
          .map(d => d.data.device_id);
        const uniqueSystemDeviceIds = Array.from(new Set(systemDeviceIds));
        const regularDeviceIds = devices
          .filter(d => d.data.device_id !== 'system' && d.data.device_id !== property.property_id)
          .map(d => d.data.device_id);
        const uniqueRegularDeviceIds = Array.from(new Set(regularDeviceIds));
        const regularEntityCount = Object.entries(entitiesByDevice)
          .filter(([deviceId]) => !uniqueSystemDeviceIds.includes(deviceId)).reduce((a, [_, es]) => a + es.size, 0);
        const systemDeviceNames = uniqueSystemDeviceIds.join(', ');
        
        const newLogs: { timestamp: number, text: string }[] = [];
        
        // Only add system devices log if there are system devices
        if (uniqueSystemDeviceIds.length > 0) {
          newLogs.push({
            timestamp: Date.now(),
            text: `Loaded ${uniqueSystemDeviceIds.length} system devices: ${systemDeviceNames}`,
          });
        }
        
        // Only add regular devices log if there are regular devices
        if (uniqueRegularDeviceIds.length > 0) {
          const buildDeviceText = () => {
            const devicePlural = uniqueRegularDeviceIds.length !== 1 ? 's' : '';
            let text = `Loaded ${uniqueRegularDeviceIds.length} device${devicePlural}`;
            if (regularEntityCount > 0) {
              const entityPlural = regularEntityCount !== 1 ? 'ies' : 'y';
              text += ` with ${regularEntityCount} entit${entityPlural}`;
            }
            return text + '.';
          };
          
          newLogs.push({
            timestamp: Date.now(),
            text: buildDeviceText(),
          });
        }
        
        setLogs(logs => [...logs, ...newLogs]);
      }
      prevDevices.current = entitiesByDevice;
    }
  }, [entitiesByDevice, devices, property.property_id]);

  return (
    <Stack justifyContent="space-between" alignItems="center" sx={{ height: '530px', width: '33%' }}>
      <Box
        sx={{
            background: '#f5f5f5',
            color: '#111',
            fontFamily: 'monospace',
            fontSize: 14,
            borderRadius: 1,
            p: 2,
            height: '500px',
            minWidth: 320,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 2,
            border: '1px solid #ccc',
        }}
        >
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Device Activity
        </Typography>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {logs.map((log, idx) => (
            <Box key={log.timestamp + '-' + idx} component="div" sx={{ whiteSpace: 'pre' }}>
                [{formatDate(log.timestamp).format('HH:mm:ss')}] {log.text}
            </Box>
            ))}
        </Box>
      </Box>
    </Stack>
  );
};

export default DeviceEventLog; 